/**
 * composerSettings — the pure seam between a chat's PERSISTED settings (the BE
 * `chat_data` row) and the Composer's live selection state.
 *
 * **Role in the chat module.** The Composer's per-turn selections — which tools are
 * selected, the knowledge toggle, the picked model, the image-gen model — are chosen by
 * the user and PERSISTED by the BE on every send (they ride the `chat-msg` payload).
 */

import { DEFAULT_KNOWLEDGE_TYPE } from 'src/content/ChatV2/constants';
import {
  CHAT_TOOL,
  IMAGE_GEN_TOOL,
  KNOWLEDGE_TYPE
} from 'src/content/ChatV2/enums';
import type { SendMessagePayload } from 'src/content/ChatV2/commands/SendMessageCommand';
import type { IAiModel } from 'src/types/base';
import type { ChatData } from 'src/types/chat';

import { normalizeToolSelection } from './toolbar/toolRegistry';

/** The Composer's live selection state — the fields seeded from `chat_data` and
 * then owned by the user for the rest of the session. Text / speech / uploads
 * are NOT here: those stay component-local to the Composer. */
export interface ComposerSettings {
  chatType: KNOWLEDGE_TYPE;
  activeTools: readonly CHAT_TOOL[];
  knowledge: KNOWLEDGE_TYPE;
  imageGenTool: IMAGE_GEN_TOOL;
  model: IAiModel | undefined;
  disabledConnectorCategoryIds: readonly number[];
}

/** The all-defaults selection — a brand-new chat (no `chat_data`) starts here.
 * The chat MODE uses the configurable `DEFAULT_KNOWLEDGE_TYPE` constant (NOT a
 * hardcoded value) so changing that one constant reshapes every new chat's default.
 */
export const DEFAULT_COMPOSER_SETTINGS: ComposerSettings = {
  chatType: DEFAULT_KNOWLEDGE_TYPE,
  activeTools: [],
  knowledge: KNOWLEDGE_TYPE.ENTERPRISE,
  imageGenTool: IMAGE_GEN_TOOL.NANO_BANANA,
  model: undefined,
  disabledConnectorCategoryIds: []
};

function activeToolsFromChatData(chatData: ChatData): CHAT_TOOL[] {
  const tools: CHAT_TOOL[] = [];
  if (chatData.web_search_enabled) {
    tools.push(CHAT_TOOL.WEB_SEARCH);
  }
  if (chatData.image_generation_tool) {
    tools.push(CHAT_TOOL.IMAGE_GENERATION);
  }
  return normalizeToolSelection(tools);
}

/**
 * A SEED that starts the composer with a given set of tools selected — i.e. the
 * `chat_data` fields `activeToolsFromChatData` above would decode back into those
 * tools.
 */
export function toolSelectionSeed(
  tools: readonly CHAT_TOOL[],
  imageGenTool: IMAGE_GEN_TOOL = IMAGE_GEN_TOOL.NANO_BANANA
): Partial<ChatData> {
  return {
    web_search_enabled: tools.includes(CHAT_TOOL.WEB_SEARCH),
    image_generation_tool: tools.includes(CHAT_TOOL.IMAGE_GENERATION)
      ? imageGenTool
      : ''
  } as Partial<ChatData>;
}

/**
 * The `chat_data` fields a sent turn implies — the inverse of how
 * `seedComposerSettings` reads `chat_data` — so a cached page re-seeds the model,
 * tools and knowledge mode the last send actually used.
 */
export function chatDataSeedOf(
  payload: SendMessagePayload
): Record<string, unknown> {
  return {
    ...(payload.modelId !== undefined && { model_id: payload.modelId }),
    web_search_enabled: !!payload.webSearchEnabled,
    image_generation_tool: payload.imageGenerationEnabled
      ? payload.imageGenerationTool ?? IMAGE_GEN_TOOL.NANO_BANANA
      : '',
    chat_type: payload.chatType
  };
}

function imageGenToolFromChatData(_chatData: ChatData): IMAGE_GEN_TOOL {
  return IMAGE_GEN_TOOL.NANO_BANANA;
}

function disabledConnectorCategoryIdsFromChatData(
  chatData: ChatData
): number[] {
  return Array.isArray(chatData.disabled_connector_categories)
    ? chatData.disabled_connector_categories
    : [];
}

/** The chat's knowledge MODE from the persisted `chat_type`. Distinct from the
 * per-turn `knowledge` toggle — this gates whether the switcher shows at all.
 * Compares raw strings for the same cross-enum reason as above. */
export function chatTypeFromChatData(
  chatData: ChatData | undefined
): KNOWLEDGE_TYPE {
  const raw = chatData?.chat_type as string | undefined;
  if (
    raw === (KNOWLEDGE_TYPE.WORLD as string) ||
    raw === (KNOWLEDGE_TYPE.ENTERPRISE as string) ||
    raw === (KNOWLEDGE_TYPE.ASSISTANT as string)
  ) {
    return raw as KNOWLEDGE_TYPE;
  }
  return DEFAULT_KNOWLEDGE_TYPE;
}

/**
 * Decode a chat's persisted `chat_data` into the Composer's initial selection. Pure —
 * no React, no store — so it's trivially testable and callable from both the provider
 * (composer fields) and ConversationView (chat mode).
 */
export function seedComposerSettings(
  chatData: ChatData | undefined,
  configuredModels: IAiModel[] | undefined
): ComposerSettings {
  if (!chatData) {
    return DEFAULT_COMPOSER_SETTINGS;
  }
  const model =
    chatData.model_id != null
      ? configuredModels?.find((m) => m.id === chatData.model_id)
      : undefined;
  return {
    chatType: chatTypeFromChatData(chatData),
    activeTools: activeToolsFromChatData(chatData),
    knowledge: KNOWLEDGE_TYPE.ENTERPRISE,
    imageGenTool: imageGenToolFromChatData(chatData),
    model,
    disabledConnectorCategoryIds:
      disabledConnectorCategoryIdsFromChatData(chatData)
  };
}
